import * as React from 'react';
import {
    Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link as MuiLink,
    Stack, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getDashboardApiError } from '../../Api';
import {
    BetupFeedMatchDto, BetupFeedPlayerDto, BetupFeedRankingDto, BetupFeedReviewCategory, BetupFeedReviewer,
    BetupFeedReviewStatus, BetupFeedTeamDto,
} from '../../models/dtos/BetupFeedPostDto';

export function formatFeedDate(value?: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function formatUsd(value: number | null | undefined, digits = 4): string {
    if (value == null || Number.isNaN(value)) return '—';
    return '$' + value.toFixed(digits);
}

export function formatCount(value: number | null | undefined): string {
    return value == null ? '—' : value.toLocaleString('en-US');
}

/** The error text of the last failed GET under `label`, for an Alert. */
export function loadErrorText(label: string, fallback: string): string {
    const info = getDashboardApiError(label);
    if (info == null) return fallback;
    const detail = info.status != null ? `HTTP ${info.status}${info.bodySnippet ? ': ' + info.bodySnippet : ''}` : info.message;
    return detail ? `${fallback} (${detail})` : fallback;
}

export function RankingChips({ ranking }: { ranking?: BetupFeedRankingDto | null }) {
    if (ranking == null) return null;
    const places: Array<[string, number | null]> = [['Day', ranking.day], ['Week', ranking.week], ['Month', ranking.month]];
    const shown = places.filter(([, place]) => place != null);
    if (shown.length === 0) return null;
    return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
            {shown.map(([label, place]) => (
                <Chip key={label} size="small" color="warning" variant="outlined" label={`${label} #${place}`} />
            ))}
        </Stack>
    );
}

/** Avatar, name (links to the user page), level and optional ranking chips. */
export function FeedPlayer({ player, showRanking = false, dense = false }: {
    player: BetupFeedPlayerDto | null | undefined;
    showRanking?: boolean;
    dense?: boolean;
}) {
    if (player == null) return <Typography variant="body2" color="text.secondary">unknown</Typography>;
    const size = dense ? 28 : 40;
    return (
        <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar src={player.photo_url ?? undefined} alt={player.name ?? ''} sx={{ width: size, height: size }}>
                {(player.name ?? '?').slice(0, 1).toUpperCase()}
            </Avatar>
            <Stack sx={{ minWidth: 0 }}>
                <MuiLink component={RouterLink} to={`/users/${player.id}`} underline="hover" variant="body2"
                    sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {player.name || `#${player.id}`}
                </MuiLink>
                <Typography variant="caption" color="text.secondary">
                    #{player.id}{player.level != null ? ` · Lv ${player.level}` : ''}{player.country_iso ? ` · ${player.country_iso}` : ''}
                </Typography>
                {showRanking && <Box sx={{ mt: 0.5 }}><RankingChips ranking={player.ranking} /></Box>}
            </Stack>
        </Stack>
    );
}

export function ReviewStatusChip({ status }: { status?: BetupFeedReviewStatus | string | null }) {
    switch (status) {
        case 'APPROVED': return <Chip size="small" color="success" label="Approved" />;
        case 'REJECTED': return <Chip size="small" color="error" label="Rejected" />;
        case 'PENDING': return <Chip size="small" color="warning" label="Pending" />;
        default: return <Chip size="small" variant="outlined" label={status ?? '—'} />;
    }
}

export const REVIEW_CATEGORY_LABELS: Record<BetupFeedReviewCategory, string> = {
    OFFENSIVE: 'Offensive',
    SEXUAL: 'Sexual',
    RACIST: 'Racist',
    VIOLENT: 'Violent',
    ILLEGAL: 'Illegal',
    REAL_MONEY_FAQ: 'Real-money questions',
    OTHER: 'Other',
};
const REVIEW_CATEGORIES = Object.keys(REVIEW_CATEGORY_LABELS) as BetupFeedReviewCategory[];

export function CategoryChips({ categories }: { categories?: string[] | null }) {
    if (!categories || categories.length === 0) return null;
    return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
            {categories.map(c => (
                <Chip key={c} size="small" color="error" variant="outlined"
                    label={REVIEW_CATEGORY_LABELS[c as BetupFeedReviewCategory] ?? c} />
            ))}
        </Stack>
    );
}

/** Who made the last decision: Claude, an operator (a rejection is then final), or nobody (review off). */
export function ReviewedByChip({ reviewer }: { reviewer?: BetupFeedReviewer | string | null }) {
    switch (reviewer) {
        case 'MODEL': return <Chip size="small" variant="outlined" label="by Claude" />;
        case 'ADMIN': return <Chip size="small" variant="outlined" color="secondary" label="by admin" />;
        case 'AUTO': return <Chip size="small" variant="outlined" label="auto (review off)" />;
        default: return null;
    }
}

const MAX_REJECT_REASON = 512;

/**
 * An operator rejects a post (pending, approved after reports, or overriding Claude): a reason and at least one
 * category are required — the author sees both. The post leaves the feed and its open reports are marked reviewed.
 */
export function RejectPostDialog({ open, subject, initialCategories, onClose, onConfirm }: {
    open: boolean;
    subject: React.ReactNode;
    initialCategories?: string[] | null;
    onClose: () => void;
    onConfirm: (reason: string, categories: BetupFeedReviewCategory[]) => void;
}) {
    const [reason, setReason] = React.useState('');
    const [categories, setCategories] = React.useState<BetupFeedReviewCategory[]>([]);

    React.useEffect(() => {
        if (!open) return;
        setReason('');
        setCategories((initialCategories ?? [])
            .filter((c): c is BetupFeedReviewCategory => REVIEW_CATEGORIES.includes(c as BetupFeedReviewCategory)));
    }, [open, initialCategories]);

    const toggle = (category: BetupFeedReviewCategory) => setCategories(current =>
        current.includes(category) ? current.filter(c => c !== category) : [...current, category]);
    const valid = reason.trim().length > 0 && categories.length > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Reject post?</DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    {subject}
                    <br />
                    The post leaves the feed and its open reports are marked reviewed. The author sees the reason and
                    the categories, and can no longer edit the post — only delete it.
                </DialogContentText>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Categories (at least one)</Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                    {REVIEW_CATEGORIES.map(c => (
                        <Chip
                            key={c}
                            label={REVIEW_CATEGORY_LABELS[c]}
                            color={categories.includes(c) ? 'error' : 'default'}
                            variant={categories.includes(c) ? 'filled' : 'outlined'}
                            onClick={() => toggle(c)}
                            aria-pressed={categories.includes(c)}
                        />
                    ))}
                </Stack>
                <TextField
                    label="Reason (shown to the author)"
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 2 }}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    inputProps={{ maxLength: MAX_REJECT_REASON }}
                    helperText={`Short and in English, like Claude's reasons. ${reason.length} / ${MAX_REJECT_REASON}`}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" color="error" disabled={!valid}
                    onClick={() => onConfirm(reason.trim(), categories)}>Reject</Button>
            </DialogActions>
        </Dialog>
    );
}

const MATCH_STATES: Record<number, string> = { 1: 'scheduled', 2: 'live', 3: 'finished', 4: 'awaiting results', 5: 'cancelled' };

function formatScore(value: number | null | undefined) {
    return value == null ? '' : String(value);
}

/** "Home – Away · League · 2:1 (finished)". */
export function matchLabel(match: BetupFeedMatchDto): string {
    const teams = `${match.home_team?.name ?? '?'} – ${match.away_team?.name ?? '?'}`;
    const score = match.score_home != null || match.score_away != null
        ? ` · ${formatScore(match.score_home)}:${formatScore(match.score_away)}` : '';
    const state = MATCH_STATES[match.state] ? ` (${MATCH_STATES[match.state]})` : '';
    return `${teams}${match.league_name ? ' · ' + match.league_name : ''}${score}${state}`;
}

/** The match or team a post is linked to, or a dash. */
export function LinkedEntity({ match, team }: { match?: BetupFeedMatchDto | null; team?: BetupFeedTeamDto | null }) {
    if (match != null) {
        return (
            <Stack>
                <Typography variant="caption" color="text.secondary">Match #{match.id}</Typography>
                <MuiLink component={RouterLink} to={`/matches/${match.id}`} underline="hover" variant="body2">{matchLabel(match)}</MuiLink>
            </Stack>
        );
    }
    if (team != null) {
        return (
            <Stack direction="row" gap={1} alignItems="center">
                {team.photo_url && <Avatar src={team.photo_url} variant="rounded" sx={{ width: 20, height: 20 }} />}
                <Typography variant="body2">Team: {team.name ?? `#${team.id}`}</Typography>
            </Stack>
        );
    }
    return <Typography variant="body2" color="text.secondary">—</Typography>;
}

/** A thumbnail that opens the full image in a dialog. */
export function FeedImage({ url, expired, size = 96, full = false }: {
    url?: string | null;
    expired?: boolean | null;
    size?: number;
    /** Render at the container width instead of a square thumbnail. */
    full?: boolean;
}) {
    const [open, setOpen] = React.useState(false);
    if (!url) {
        return expired
            ? <Chip size="small" variant="outlined" label="image expired" />
            : null;
    }
    return (
        <>
            <Box
                component="img"
                src={url}
                alt="post"
                loading="lazy"
                onClick={() => setOpen(true)}
                sx={full
                    ? { width: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', display: 'block' }
                    : { width: size, height: size, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', display: 'block' }}
            />
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
                <DialogContent sx={{ p: 1 }}>
                    <Box component="img" src={url} alt="post" sx={{ maxWidth: '100%', maxHeight: '85vh', display: 'block' }} />
                </DialogContent>
                <DialogActions>
                    <Button href={url} target="_blank" rel="noreferrer">Open original</Button>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export function ConfirmDialog({ open, title, text, confirmLabel, confirmColor = 'primary', onConfirm, onClose, children }: {
    open: boolean;
    title: string;
    text: React.ReactNode;
    confirmLabel: string;
    confirmColor?: 'primary' | 'error' | 'success' | 'warning';
    onConfirm: () => void;
    onClose: () => void;
    children?: React.ReactNode;
}) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText component="div">{text}</DialogContentText>
                {children}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" color={confirmColor} onClick={onConfirm}>{confirmLabel}</Button>
            </DialogActions>
        </Dialog>
    );
}

/** Post text as written: full, whitespace preserved. */
export function PostText({ text }: { text?: string | null }) {
    if (!text) return <Typography variant="body2" color="text.secondary">(no text)</Typography>;
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</Typography>;
}

/** Cuts a long text for a confirm dialog. */
export function shorten(text: string | null | undefined, max = 140): string {
    if (!text) return '';
    return text.length > max ? text.slice(0, max - 1) + '…' : text;
}
